import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router";
import { AlertTriangle, Loader2, Play, Trash2, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { LinkedInDropzone } from "@/components/linkedin/LinkedInDropzone";
import { linkedInApi, type HashMatch } from "@/services/linkedin-api";
import { computeFileHash } from "@/lib/s3-upload";
import { toUserError } from "@/lib/api-error";

interface QueuedFile {
  id: string;
  file: File;
  hash?: string;
  hashing: boolean;
  /** Match com upload anterior (se já foi processado antes). */
  duplicate?: HashMatch;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface LinkedInUploadDialogProps {
  /** Chamado após upload concluído (invalidar queries, voltar pra aba Pendentes). */
  onUploaded: (deferred: boolean) => void;
}

/**
 * Botão "Novo upload" + dialog com dropzone, fila local e progresso.
 * Nada sobe pro S3 nem dispara extração até o usuário clicar "Iniciar".
 */
export function LinkedInUploadDialog({ onUploaded }: LinkedInUploadDialogProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  // Fila local: arquivos aguardando o usuário clicar "Iniciar processamento".
  const [queue, setQueue] = useState<QueuedFile[]>([]);
  // Progresso de upload por arquivo (S3 direto, depois do "Iniciar").
  const [uploadProgress, setUploadProgress] = useState<
    { filename: string; pct: number }[]
  >([]);
  // Modo "subir agora, processar depois": jobs ficam em pending até clicar
  // "Iniciar processamento de pendentes" na tabela da página.
  const [processLater, setProcessLater] = useState(false);

  // Adiciona arquivos à fila (em vários arrastes/seleções) e, em segundo plano,
  // calcula o hash e checa se já foram processados antes.
  const handleAddFiles = async (files: File[]) => {
    const novos: QueuedFile[] = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
      hashing: true,
    }));
    setQueue((prev) => [...prev, ...novos]);

    try {
      const comHash = await Promise.all(
        novos.map(async (item) => ({ id: item.id, hash: await computeFileHash(item.file) })),
      );
      setQueue((prev) =>
        prev.map((item) => {
          const found = comHash.find((resultado) => resultado.id === item.id);
          return found ? { ...item, hash: found.hash, hashing: false } : item;
        }),
      );

      const hashes = comHash.map((resultado) => resultado.hash);
      const { matches } = await linkedInApi.checkHashes(hashes);
      if (Object.keys(matches).length > 0) {
        setQueue((prev) =>
          prev.map((item) =>
            item.hash && matches[item.hash] ? { ...item, duplicate: matches[item.hash] } : item,
          ),
        );
      }
    } catch (erro) {
      // Falha ao calcular hash/checar duplicados não impede o upload — só some o aviso.
      console.warn("[linkedin] falha ao checar duplicados:", erro);
      setQueue((prev) => prev.map((item) => (item.hashing ? { ...item, hashing: false } : item)));
    }
  };

  const upload = useMutation({
    mutationFn: async (params: { files: File[]; deferred: boolean }) => {
      const { files, deferred } = params;
      setUploadProgress(files.map((file) => ({ filename: file.name, pct: 0 })));
      // uploadSingle não suporta deferred (1 arquivo => não há ganho); usa
      // sempre a rota de batch quando o usuário pede "processar depois".
      if (files.length === 1 && !deferred) {
        const job = await linkedInApi.uploadSingle(files[0], {
          onProgress: (pct) => setUploadProgress([{ filename: files[0].name, pct }]),
        });
        return { jobs: [job], total: 1, deferred };
      }
      const result = await linkedInApi.uploadBatch(
        files,
        ({ index, filename, pct }) =>
          setUploadProgress((prev) => {
            const next = [...prev];
            next[index] = { filename, pct };
            return next;
          }),
        { deferred },
      );
      return { ...result, deferred };
    },
    onSuccess: (result) => {
      toast({
        title: `${result.total} PDF(s) enviado(s)`,
        description: result.deferred
          ? "Em fila (pending). Clique em 'Iniciar processamento de pendentes' quando quiser."
          : "Processamento iniciado. Acompanhe na lista de pendentes.",
      });
      setUploadProgress([]);
      setQueue([]);
      setOpen(false);
      onUploaded(result.deferred);
    },
    onError: (erro: Error) => {
      setUploadProgress([]);
      toast({ title: "Erro no upload", description: toUserError(erro).description, variant: "destructive" });
    },
  });

  const isUploading = upload.isPending;

  const handleStart = () => {
    if (queue.length === 0) return;
    upload.mutate({ files: queue.map((item) => item.file), deferred: processLater });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Não deixa fechar no meio do upload pra não perder o progresso de vista.
        if (isUploading) return;
        setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <Button className="gap-1.5">
          <Upload className="h-4 w-4" />
          Novo upload
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Novo upload de PDFs</DialogTitle>
          <DialogDescription>
            Adicione PDFs de perfis do LinkedIn à fila e clique em "Iniciar processamento".
            Vale tanto o PDF exportado pelo LinkedIn quanto a impressão da página do perfil
            (Ctrl+P no navegador).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <LinkedInDropzone multiple disabled={isUploading} onFiles={handleAddFiles} />

          <p className="text-xs text-muted-foreground">
            Dica: se for imprimir o perfil com Ctrl+P, antes clique em "Exibir tudo" nas
            seções do perfil (Experiência, Competências, Formação). O que estiver recolhido
            na página não entra no PDF.
          </p>

          {queue.length > 0 && (
            <div className="space-y-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-sm font-medium">
                  {queue.length} arquivo{queue.length > 1 ? "s" : ""} na fila
                </span>
                <div className="flex items-center gap-3">
                  <label
                    htmlFor="process-later"
                    className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer"
                  >
                    <Checkbox
                      id="process-later"
                      checked={processLater}
                      onCheckedChange={(checked) => setProcessLater(!!checked)}
                      disabled={isUploading}
                    />
                    Subir agora, processar depois
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setQueue([])}
                    disabled={isUploading}
                  >
                    Limpar
                  </Button>
                  <Button size="sm" onClick={handleStart} disabled={isUploading} className="gap-1.5">
                    {isUploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                    {processLater
                      ? `Subir ${queue.length} (sem processar)`
                      : `Iniciar processamento (${queue.length})`}
                  </Button>
                </div>
              </div>

              <div className="divide-y rounded-md border max-h-[40vh] overflow-y-auto">
                {queue.map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs truncate" title={item.file.name}>
                          {item.file.name}
                        </span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatBytes(item.file.size)}
                        </span>
                      </div>
                      {item.hashing ? (
                        <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" /> verificando duplicados...
                        </span>
                      ) : item.duplicate ? (
                        <span className="inline-flex flex-wrap items-center gap-1.5 text-xs text-amber-700">
                          <Badge
                            variant="outline"
                            className="border-amber-300 bg-amber-50 text-amber-700"
                          >
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Já processado antes
                          </Badge>
                          {item.duplicate.created_at && (
                            <span className="text-muted-foreground">
                              {new Date(item.duplicate.created_at).toLocaleDateString("pt-BR")}
                            </span>
                          )}
                          {item.duplicate.freelancer_id && (
                            <Link
                              to={`/freelancers/${item.duplicate.freelancer_id}`}
                              target="_blank"
                              className="text-primary underline hover:no-underline"
                            >
                              ver freelancer
                            </Link>
                          )}
                        </span>
                      ) : null}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      aria-label="Remover da fila"
                      onClick={() => setQueue((prev) => prev.filter((outro) => outro.id !== item.id))}
                      disabled={isUploading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {uploadProgress.length > 0 && (
            <div className="space-y-2 max-h-[30vh] overflow-y-auto">
              {uploadProgress.map((progresso, index) => (
                <div key={index} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="font-mono truncate max-w-md">{progresso.filename}</span>
                    <span className="text-muted-foreground">{progresso.pct}%</span>
                  </div>
                  <Progress value={progresso.pct} className="h-1.5" />
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
